import { useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { createListCollection } from "@ark-ui/react";

import { LineGraphEdrLine } from "@/lib/api";
import { useDatasetsQuery, useGetCapabilitiesQuery } from "@/features/admin/hooks/queries";
import { SelectItem } from "@/features/shared/components/mui-rff-fields/mui-rff-fields";
import Select from "@/components/select/select";
import { required } from "@/features/shared/components/mui-rff-fields/validation-helpers";
import { useTranslation } from "@/lib/i18n";

interface EDRConfigurationsProps {
  name: string;
  initialValue: LineGraphEdrLine;
  type: string;
}

export const EDRConfiguration = ({ name, initialValue, type }: EDRConfigurationsProps) => {
  const { t } = useTranslation();
  const form = useForm();
  const linegraphDatasetQuery = useDatasetsQuery(type);

  const edrDatasetIdField = useField(`${name}.edr_dataset_id`, {
    initialValue: initialValue.edr_dataset_id,
    validate: required,
  });
  const edrDatasetId = useMemo(() => {
    if (!linegraphDatasetQuery.data) return undefined;

    return linegraphDatasetQuery.data.find(
      (dataset) => dataset.id === edrDatasetIdField.input.value,
    )?.id;
  }, [edrDatasetIdField, linegraphDatasetQuery.data]);

  const datasetQueryOptions = useMemo(() => {
    if (!linegraphDatasetQuery.data) return [];

    return linegraphDatasetQuery.data.map((dataset) => ({
      label: dataset.name,
      value: dataset.id,
    }));
  }, [linegraphDatasetQuery.data]);
  const datasetQueryCollection = createListCollection({
    items: datasetQueryOptions,
  });

  const edrParamField = useField(`${name}.edr_parameter`, {
    initialValue: initialValue.edr_parameter,
    validate: required,
    multiple: true,
  });
  const edrParamsQuery = useGetCapabilitiesQuery(edrDatasetId ?? "edr_collection_id");
  const edrParamsOptions: SelectItem[] = useMemo(() => {
    if (!edrParamsQuery.data?.parameters) return [];

    return edrParamsQuery.data.parameters.map(
      (params) =>
        ({
          label: params.description,
          value: params.id,
          group: params.unit.symbol,
        }) as SelectItem,
    );
  }, [edrParamsQuery.data]);
  const edrParamsCollection = createListCollection({
    items: edrParamsOptions,
    groupBy: (item) => item.group ?? "",
  });

  if (linegraphDatasetQuery.isLoading && !linegraphDatasetQuery.isSuccess)
    return <p>{t("admin.widgets.fields.lineGraph.lines.loading")}</p>;

  if (linegraphDatasetQuery.isError)
    return <p>{t("admin.widgets.fields.lineGraph.lines.dataError")}</p>;

  return (
    <>
      <Select
        name={edrDatasetIdField.input.name}
        value={edrDatasetIdField.input.value}
        error={edrDatasetIdField.meta.touched && edrDatasetIdField.meta.error}
        label={t("admin.widgets.fields.lineGraph.lines.dataset")}
        onValueChange={(value) => form.change(edrDatasetIdField.input.name, value)}
        collection={datasetQueryCollection}
      />
      {edrDatasetIdField.input.value.length > 0 && (
        <Select
          name={edrParamField.input.name}
          value={edrParamField.input.value}
          error={edrParamField.meta.touched && edrParamField.meta.error}
          label={t("admin.widgets.fields.lineGraph.lines.parameters")}
          onValueChange={(value) => form.change(edrParamField.input.name, value)}
          collection={edrParamsCollection}
        />
      )}
    </>
  );
};
